import * as SentryNextJS from '@sentry/nextjs';

export const config: Parameters<typeof SentryNextJS.withSentryConfig>[1] = {
  // For all available options, see:
  // https://docs.sentry.io/platforms/javascript/guides/nextjs/manual-setup/
  org: 'anchor-labs',
  // Only print logs for uploading source maps in CI
  silent: !process.env.CI,
  authToken: process.env.SENTRY_AUTH_TOKEN,
  // Upload a larger set of source maps for prettier stack traces (increases build time)
  widenClientFileUpload: true,
  // Automatically annotate React components to show their full name in breadcrumbs and session replay
  reactComponentAnnotation: {
    enabled: true,
  },
  // Route browser requests to Sentry through a Next.js rewrite to circumvent ad-blockers.
  // This can increase your server load as well as your hosting bill.
  // Note: Check that the configured route will not match with your Next.js middleware, otherwise reporting of client-
  // side errors will fail.
  // tunnelRoute: "/monitoring",

  // Automatically tree-shake Sentry logger statements to reduce bundle size
  disableLogger: true,

  // Enables automatic instrumentation of Vercel Cron Monitors. (Does not yet work with App Router route handlers.)
  // See the following for more information:
  // https://docs.sentry.io/product/crons/
  // https://vercel.com/docs/cron-jobs
  automaticVercelMonitors: true,
  telemetry: false,
  sourcemaps: {
    // when this env variable is defined, we are making a deployment
    disable: !process.env.ENVIRONMENT,
    // scripts/deploy-frontend.sh deletes every .map from the export before it
    // syncs to the bucket, so maps still never ship publicly. Leaving deletion
    // to that one place keeps them readable by the Datadog RUM upload that runs
    // there, instead of depending on when this plugin removes them.
    deleteSourcemapsAfterUpload: false,
  },
};

export type userContext = {
  // org-scoped userKeyId
  id: string;
  // Org-agnostic person identifier. Optional — when absent we fall back to `id`
  // for Sentry's user.id, since client apps no longer fetch `Person`.
  personID?: string;

  orgID: string;
  isPortoOrg?: boolean;
};

// Set the user context for Sentry to be included in all logs
export function setUserContext({
  id,
  orgID,
  isPortoOrg,
  personID,
}: userContext): void {
  // Some environments won't have sentry setup (eg testing)
  if (
    SentryNextJS === undefined ||
    SentryNextJS.setUser === undefined ||
    SentryNextJS.setTag === undefined
  ) {
    console.warn('Sentry is not initialized, skipping user context setup');
    return;
  }

  const { setTag, setUser } = SentryNextJS;
  setUser({
    id: personID || id,
    // We currently exclude PII to avoid any privacy concerns
    // On-call can search the ID in backoffice to get the person/org
  });

  if (personID) {
    setTag('personID', personID);
  }
  setTag('orgUserID', id);
  setTag('orgID', orgID);

  if (isPortoOrg) {
    setTag('isPortoOrg', isPortoOrg);
  }
}
